import { Injectable, OnDestroy } from '@angular/core';
import { CanActivate, CanDeactivate, ActivatedRouteSnapshot, Router, RouterStateSnapshot, UrlTree } from '@angular/router';

import { from, Observable, of, Subscription, TeardownLogic } from 'rxjs';
import { take } from 'rxjs/operators';

import { UserService } from 'services/user/user.service';
import { UtilitiesService } from 'services/utilities/utilities.service';
import { RoutePaths } from './route.roles';

@Injectable()
export class AuthenticatedGuard implements CanActivate, OnDestroy {
    private _subrelease: Subscription = new Subscription();
    set subrelease(sub: TeardownLogic) {
        this._subrelease.add(sub);
    }

    private _isAuthenticated = false; // Default to not authenticated.
    private _userRoles: string[] = [];

    constructor(private router: Router, private userSvc: UserService) {}

    canActivate(route: ActivatedRouteSnapshot, state: RouterStateSnapshot): boolean | UrlTree {
        // At a minimum, only allow access if we have a valid authtoken stored for the current user.
        let routeOk = !!this.userSvc.User?.AuthToken;

        // Without an authtoken, we redirec the user to the landing route automatically.
        if (!this.userSvc.User?.AuthToken) {
            return this.router.createUrlTree([`/${RoutePaths.Landing}`]);
        }

        // If there is optional data present, particularly the 'Roles' setting, we must also check to ensure that the
        // current user has a role that is in the provided list of necessary roles to use the route.
        const key = 'Roles';

        if (routeOk && route.data != null && route.data[key] != null) {
            // Does the user have a role that is in the provided list?
            const neededRoles = (route.data[key] || []) as string[];

            if (neededRoles.includes('all') && !this._userRoles?.length) {
                routeOk = false;
            } else if (!neededRoles.includes('all') && !neededRoles.some((nr) => !!this._userRoles.find((ur) => ur === nr))) {
                // Cannot find any of the needed roles within the list of user owned roles. That is a failure.
                routeOk = false;
            }
        }

        if (!routeOk) {
            console.error('You are not authorized to view the requested page.');
        }

        return routeOk;
    }

    ngOnDestroy(): void {
        this._subrelease.unsubscribe();
    }
}

export interface UnsavedChanges {
    CanDeactivate: () => Observable<boolean> | Promise<boolean> | boolean;
    ChangeTrackingActive: boolean;
    SetupTracking: () => void;
    TakeDownTracking: () => void;
}

@Injectable()
export class UnsavedChangesGuard implements CanDeactivate<UnsavedChanges> {
    constructor(private unsavedSvc: UnsavedChangesService) {}

    canDeactivate(target: UnsavedChanges): Observable<boolean> | Promise<boolean> | boolean {
        if (!this.unsavedSvc.HasTracker()) {
            return true;
        }

        const dresult = target.CanDeactivate ? target.CanDeactivate() : true;

        return dresult;
    }
}

@Injectable()
export class UnsavedChangesService {
    private _nativeListener: EventListenerOrEventListenerObject | null;
    private _tracker: null | (() => Observable<boolean> | Promise<boolean> | boolean) = null;

    // tslint:disable-next-line: no-any
    private _nativeChecker = (checker: any, event: any) => {
        const isClean = checker != null ? checker(true) : false;

        if (!isClean) {
            const msg = 'There may be unsaved changes on this page. Do you wish to continue leaving the page?';

            // tslint:disable-next-line: deprecation
            event = event || window.event;

            if (event) {
                event.returnValue = msg;
            }

            return msg;
        } else {
            return;
        }
        // tslint:disable-next-line: semicolon
    };

    constructor() {
        this._nativeListener = null;
    }

    // #region Clear Tracking Method

    ClearTracking(): void {
        if (this._nativeListener) {
            window.removeEventListener('beforeunload', this._nativeListener);
            this._nativeListener = null;
        }

        this._tracker = null;
    }

    // #endregion

    // #region Check Tracker Method

    CheckTracker(): Observable<boolean> {
        const self = this;

        if (self._tracker != null) {
            const result = self._tracker();
            let obsResult: Observable<boolean>;

            if (result instanceof Promise) {
                obsResult = from(result);
            } else if (!(result instanceof Observable)) {
                obsResult = of<boolean>(result).pipe(take(1));
            } else {
                obsResult = result as Observable<boolean>;
            }

            return obsResult.pipe(take(1));
        } else {
            return of<boolean>(true).pipe(take(1));
        }
    }

    // #endregion

    // #region Has Tracker

    HasTracker(): boolean {
        return this._tracker != null;
    }

    // #endregion

    // #region Set Tracking Method

    SetTracking(tracker: () => Observable<boolean> | Promise<boolean> | boolean): void {
        const self = this;

        if (self._nativeListener) {
            self.ClearTracking();
        }

        self._nativeListener = self._nativeChecker.bind(window, tracker);

        window.addEventListener('beforeunload', self._nativeListener);

        this._tracker = tracker;
    }

    // #endregion
}
